import { ModalsService } from './../../../shared/modals/modals.service';
import { identity } from 'lodash-es';
import { getCurrentUser, getCurrentUserLoadedStatus, getCurrentUserWithOpScopesLoadedStatus } from 'Src/ng2/store/selectors/current-user-selectors';
import { LoadCurrentUser, LoadCurrentUserWithOpScopes } from './../../../store/actions/current-user-actions';
import { Auth } from 'Src/ng2/shared/auth/auth.service';
import { ImUser } from './../../../shared/services/im-models/im-user';
import { IUser } from 'Src/ng2/shared/typings/interfaces/user.interface';
import { LoadToggles } from './../../../store/actions/toggle-actions';
import { tap, filter, switchMap, take, map } from 'rxjs/operators';
import { getToggleLoadedStatus, getToggleEntities } from './../../../store/selectors/toggles-selectors';
import { Store, select } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { CanActivate, UrlTree, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, of, forkJoin, combineLatest } from 'rxjs';
import { UrlPathService } from 'Src/ng2/shared/services/url-path-service/url-path.service';
import { PartnerTypes } from 'Src/ng2/shared/typings/interfaces/partner.interface';
import { ToggleService } from 'Src/ng2/shared/services/toggle/toggle.service';
import { TOpEntityType } from 'Src/ng2/shared/typings/types/entity-type.types';

@Injectable()
export class HomeGuard implements CanActivate {
  constructor (
    private imUser: ImUser,
    private router: Router,
    private store: Store<any>,
    private auth: Auth,
    private modalsService: ModalsService,
    private urlPathService: UrlPathService,
    private toggleService: ToggleService,
  ) {}

  canActivate (snapshot: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<UrlTree> {
    const { noAccessUrl } = snapshot.queryParams;
    if (noAccessUrl) this.showRedirectModal(noAccessUrl);

    const currentUser$ = this.getCurrentUserFromStoreOrAPI();
    const toggles$ = this.getTogglesFromStoreOrAPI();

    return forkJoin([currentUser$, toggles$]).pipe(
      switchMap(([currentUser, toggles]) => this.redirect(currentUser, toggles, state)),
    );
  }

  private redirect (currentUser: IUser, toggles, state: RouterStateSnapshot): Observable<UrlTree> {
    const onePortalEnabled = this.toggleService.onePortalEnabled({
      entityType: null,
      entityMode: null,
      user: currentUser,
      url: state.url,
    });
    const isSuperAdmin = this.imUser.isSuperAdmin(currentUser) && this.imUser.isActive(currentUser);
    const isHybridUser = this.imUser.isHybridUser(currentUser) && this.imUser.isActive(currentUser, PartnerTypes.HYBRID);
    const isSingleSchoolUser = this.imUser.isSchoolUser(currentUser) && this.imUser.isActive(currentUser, PartnerTypes.SCHOOL);
    const isSingleMultiSchoolUser = this.imUser.isMultiSchoolUser(currentUser) && this.imUser.isSingleMultiSchoolUser(currentUser) && this.imUser.isActive(currentUser, PartnerTypes.SCHOOL);
    const isMultiSchoolUser = this.imUser.isMultiSchoolUser(currentUser) && !this.imUser.isSingleMultiSchoolUser(currentUser) && this.imUser.isActive(currentUser, PartnerTypes.SCHOOL);
    const isSchoolClusterUser = this.imUser.isClusterUser(currentUser, PartnerTypes.SCHOOL) && this.imUser.isActive(currentUser, PartnerTypes.SCHOOL);
    const isShelterClusterUser = this.imUser.isClusterUser(currentUser, PartnerTypes.SHELTER) && this.imUser.isActive(currentUser, PartnerTypes.SHELTER);
    const isSingleShelterUser = this.imUser.isShelterUser(currentUser) && this.imUser.isActive(currentUser, PartnerTypes.SHELTER);
    const isExpandEdUser = this.imUser.isExpandEd(currentUser);
    const isCUNYUser = this.imUser.isCUNY(currentUser);
    const isEntityUser = this.imUser.isEntityUser(currentUser);

    if (isEntityUser) {
      return of(this.goToEntityPicker());
    } else if (isExpandEdUser) {
      return of(this.goToExpandEdDataGrid());
    } else if (isCUNYUser) {
      return of(this.goToCUNYDataGrid());
    } else if (onePortalEnabled) {
      return this.onePortalRouteDecider(currentUser);
    } else if (isSuperAdmin) {
      return of(this.navigateSuperAdminUser());
    } else if (isHybridUser) {
      return of(this.navigateHybridUser());
    } else if (isSingleSchoolUser || isSingleMultiSchoolUser) {
      const schoolId = currentUser.nvRole.schoolId || currentUser._role_clusterSchoolIds[0];
      return of(this.goToSchoolPortal(schoolId));
    } else if (isMultiSchoolUser) {
      return of(this.goToSchoolPicker());
    } else if (isSchoolClusterUser) {
      return of(this.navigateSchoolClusterUser(toggles, currentUser));
    } else if (isSingleShelterUser) {
      const shelterId = currentUser.nvRoleShelter.shelterId;
      return of(this.goToShelterPortal(shelterId));
    } else if (isShelterClusterUser) {
      return of(this.navigateShelterClusterUser(toggles, currentUser));
    }
  }

  private onePortalRouteDecider (user: IUser): Observable<UrlTree> {
    return this.getCurrentUserWithScopesFromStoreOrAPI().pipe(
      map(currentUserWithScopes => {
        const userWithScopes = currentUserWithScopes || user;
        const scopes = userWithScopes.userScopesEntityTypeMap;
        const isEntityContextSelectable = this.imUser.isEntityContextSelectable(scopes);
        if (isEntityContextSelectable) {
          return this.goToExplore();
        } else {
          // Shared with the picker modal so the login landing and the picker cannot disagree.
          return this.goToOnePortal(this.imUser.getDefaultEntityContext(scopes));
        }
      }),
    );
  }

  private goToOnePortal (opts: {
    entityType: TOpEntityType,
    entityId?: string,
    networkId?: string,
  }): UrlTree {
    const { entityType, entityId, networkId } = opts;

    const url = this.urlPathService.getPortalUrl({ entityType, entityId, networkId });
    return this.router.createUrlTree([url]);
  }

  private getTogglesFromStoreOrAPI (): Observable<any> {
    return this.store.pipe(
      select(getToggleLoadedStatus),
      tap(loaded => {
        if (!loaded) this.store.dispatch(new LoadToggles({}));
      }),
      filter(identity),
      switchMap(() => this.store.pipe(select(getToggleEntities))),
      take(1),
    );
  }

  private getCurrentUserFromStoreOrAPI (): Observable<IUser> {
    return this.store.pipe(
      select(getCurrentUserLoadedStatus),
      tap(async loaded => {
        if (!loaded) {
          const email = await this.auth.getCurrentUserEmail();
          this.store.dispatch(new LoadCurrentUser({ email }));
        }
      }),
      filter(loaded => loaded),
      switchMap(() => this.store.pipe(select(getCurrentUser))),
      take(1),
    );
  }

  private getCurrentUserWithScopesFromStoreOrAPI (): Observable<IUser> {
    return combineLatest([
      this.store.pipe(select(getCurrentUserLoadedStatus)),
      this.store.pipe(select(getCurrentUserWithOpScopesLoadedStatus)),
    ]).pipe(
      tap(async ([isUserLoaded, isScopesLoaded]) => {
        if (!isUserLoaded) {
          const email = await this.auth.getCurrentUserEmail();
          this.store.dispatch(new LoadCurrentUser({ email }));
        }

        if (isUserLoaded && !isScopesLoaded) {
          this.store.dispatch(new LoadCurrentUserWithOpScopes());
        }
      }),
      filter(([isUserLoaded, isScopesLoaded]) => isUserLoaded && isScopesLoaded),
      take(1),
      switchMap(() => this.store.pipe(select(getCurrentUser), take(1))),
    );
  }

  private navigateSuperAdminUser (): UrlTree {
    return this.goToExplore();
  }

  private goToEntityPicker (): UrlTree {
    const url = this.urlPathService.computeDistrictUrlPath('/entity-picker');
    return this.router.createUrlTree([url]);
  }

  private navigateHybridUser (): UrlTree {
    return this.goToExplore();
  }

  private goToSchoolPortal (schoolId): UrlTree {
    const url = this.urlPathService.computeDistrictUrlPath(`/school/${schoolId}/home`);
    return this.router.createUrlTree([url]);
  }

  private navigateSchoolClusterUser (toggles, currentUser: IUser): UrlTree {
    const accessNetworkView = toggles.TOGGLE_NETWORK_DASHBOARD;
    const isNetworkOnly = this.imUser.isNetworkOnly(currentUser, PartnerTypes.SCHOOL_NETWORK);
    if (accessNetworkView && isNetworkOnly) {
      const url = this.urlPathService.computeDistrictUrlPath(`/network/school/${currentUser.nvRole.clusterId}`);
      return this.router.createUrlTree([url]);
    } else return this.goToSchoolPicker();
  }

  private navigateShelterClusterUser (toggles, currentUser: IUser): UrlTree {
    const accessNetworkView = toggles.TOGGLE_NETWORK_DASHBOARD;
    const isNetworkOnly = this.imUser.isNetworkOnly(currentUser, PartnerTypes.SHELTER_NETWORK);
    if (accessNetworkView && isNetworkOnly) {
      const url = this.urlPathService.computeDistrictUrlPath(`/network/shelter/${currentUser.nvRoleShelter.shelterClusterId}`);
      return this.router.createUrlTree([url]);
    } else return this.goToShelterPicker();
  }

  private goToShelterPortal (shelterId: string): UrlTree {
    const url = this.urlPathService.computeDistrictUrlPath(`/shelter/${shelterId}/home`);
    return this.router.createUrlTree([url]);
  }

  private showRedirectModal (noAccessUrl: string): void {
    const title = 'Unauthorized Access';
    const message = `You cannot access "${
      noAccessUrl
    }" because your role does not allow it. Please contact your administrator to request a role change.`;
    this.modalsService.openErrorModal({ title, message });
  }

  private goToSchoolPicker (): UrlTree {
    const url = this.urlPathService.computeDistrictUrlPath('/school-picker');
    return this.router.createUrlTree([url]);
  }

  private goToShelterPicker (): UrlTree {
    const url = this.urlPathService.computeDistrictUrlPath('shelter-picker');
    return this.router.createUrlTree([url]);
  }

  private goToExplore (): UrlTree {
    const url = this.urlPathService.computeDistrictUrlPath('/explore');
    return this.router.createUrlTree([url]);
  }

  private goToExpandEdDataGrid (): UrlTree {
    const url = this.urlPathService.computeDistrictUrlPath('/expand-ed-grid');
    return this.router.createUrlTree([url]);
  }

  private goToCUNYDataGrid (): UrlTree {
    const url = this.urlPathService.computeDistrictUrlPath('/cuny-grid');
    return this.router.createUrlTree([url]);
  }
}
